import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import {
  normalizePermissionIds,
  sharePermissionsDirty,
} from '../utils/studentNotesPermissions';

const BTN_CANCEL_BG = '#B0B0B0';
const BTN_SAVE_BG = '#d4af37';

const PERMISSION_ROWS = [
  {
    key: 'readPermission',
    label: 'Read Permission',
    icon: 'visibility',
  },
  {
    key: 'commentPermission',
    label: 'Comment Permission',
    icon: 'chat-bubble-outline',
  },
  {
    key: 'editPermission',
    label: 'Edit Permission',
    icon: 'edit',
  },
];

function studentLabel(s) {
  const name = s.student_name || s.username || `Student ${s.id}`;
  return s.student_grade ? `${name} · ${s.student_grade}` : name;
}

function PermissionMultiSelect({
  students,
  selectedIds,
  onChange,
  loading,
  styles,
  gold,
}) {
  const [open, setOpen] = useState(false);
  const selectedSet = new Set(normalizePermissionIds(selectedIds));

  const summary = () => {
    if (loading) return 'Loading students…';
    const picked = students.filter((s) => selectedSet.has(Number(s.id)));
    if (picked.length === 0) return 'Select students…';
    if (picked.length <= 2) {
      return picked.map((s) => s.student_name || s.username).join(', ');
    }
    return `${picked.length} students selected`;
  };

  const toggle = (id) => {
    const nid = Number(id);
    const next = new Set(selectedSet);
    if (next.has(nid)) next.delete(nid);
    else next.add(nid);
    onChange([...next]);
  };

  return (
    <View style={styles.pickWrap}>
      <TouchableOpacity
        style={styles.pickTrigger}
        onPress={() => !loading && setOpen((v) => !v)}
        activeOpacity={0.85}
        disabled={loading || students.length === 0}
        {...(Platform.OS === 'web' ? { dataSet: { studentNotesSharePick: 'true' } } : {})}
      >
        <Text
          style={styles.pickTriggerText}
          numberOfLines={2}
          {...(Platform.OS === 'web'
            ? { dataSet: { studentNotesSharePickText: 'true' } }
            : {})}
        >
          {summary()}
        </Text>
        <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={22} color={gold} />
      </TouchableOpacity>
      {open && students.length > 0 ? (
        <ScrollView
          style={styles.pickList}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
        >
          {students.map((s) => {
            const sid = Number(s.id);
            const checked = selectedSet.has(sid);
            return (
              <Pressable
                key={s.id}
                style={[styles.pickRow, checked && styles.pickRowSelected]}
                onPress={() => toggle(sid)}
                {...(Platform.OS === 'web'
                  ? { dataSet: { studentNotesSharePickRow: 'true' } }
                  : {})}
              >
                <MaterialIcons
                  name={checked ? 'check-box' : 'check-box-outline-blank'}
                  size={22}
                  color={gold}
                />
                <Text
                  style={styles.pickRowText}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { studentNotesSharePickText: 'true' } }
                    : {})}
                >
                  {studentLabel(s)}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      ) : null}
      {open && !loading && students.length === 0 ? (
        <Text style={styles.pickEmpty}>No other students available.</Text>
      ) : null}
    </View>
  );
}

/**
 * Quip-style share dialog: read / comment / edit permission multi-selects.
 */
export default function StudentNotesShareModal({
  visible,
  onClose,
  students = [],
  loadingStudents = false,
  readPermission = [],
  commentPermission = [],
  editPermission = [],
  onSave,
  saving = false,
  noteSaved = true,
  noteHasContent = true,
}) {
  const theme = useTheme();
  const gold = theme.colors.primary || '#d4af37';
  const styles = getStyles(theme, gold);

  const [draft, setDraft] = useState({
    readPermission: [],
    commentPermission: [],
    editPermission: [],
  });
  useEffect(() => {
    if (!visible) {
      return;
    }
    setDraft({
      readPermission: normalizePermissionIds(readPermission),
      commentPermission: normalizePermissionIds(commentPermission),
      editPermission: normalizePermissionIds(editPermission),
    });
  }, [visible, readPermission, commentPermission, editPermission]);

  const setField = useCallback((key, ids) => {
    setDraft((d) => ({ ...d, [key]: normalizePermissionIds(ids) }));
  }, []);

  const savedSnapshot = {
    readPermission: normalizePermissionIds(readPermission),
    commentPermission: normalizePermissionIds(commentPermission),
    editPermission: normalizePermissionIds(editPermission),
  };
  const hasChanges = sharePermissionsDirty(savedSnapshot, draft);
  const saveEnabled = hasChanges;

  const handleSave = async () => {
    if (saving || !saveEnabled) return;
    if (onSave) await onSave(draft);
  };

  const sheetContent = (
    <Pressable
      style={styles.sheet}
      onPress={(e) => e.stopPropagation?.()}
      nativeID="student-notes-share-modal-sheet"
      {...(Platform.OS === 'web'
        ? { dataSet: { studentNotesShareSheet: 'true' } }
        : {})}
    >
          <View
            style={styles.sheetHeader}
            {...(Platform.OS === 'web'
              ? { dataSet: { studentNotesShareHeader: 'true' } }
              : {})}
          >
            <MaterialIcons name="person-add" size={22} color={gold} />
            <Text
              style={styles.sheetTitle}
              {...(Platform.OS === 'web'
                ? { dataSet: { studentNotesShareTitle: 'true' } }
                : {})}
            >
              Share note
            </Text>
          </View>
          <Text
            style={styles.sheetSubtitle}
            {...(Platform.OS === 'web'
              ? { dataSet: { studentNotesShareSubtitle: 'true' } }
              : {})}
          >
            Choose which students can read, comment, or edit this note.
          </Text>
          {!noteSaved && !noteHasContent ? (
            <Text
              style={styles.saveFirstHint}
              {...(Platform.OS === 'web'
                ? { dataSet: { studentNotesShareHint: 'true' } }
                : {})}
            >
              Add a title or note body before saving sharing settings.
            </Text>
          ) : null}
          {!noteSaved && noteHasContent ? (
            <Text
              style={styles.saveFirstHint}
              {...(Platform.OS === 'web'
                ? { dataSet: { studentNotesShareHint: 'true' } }
                : {})}
            >
              Save will store your note and apply sharing below.
            </Text>
          ) : null}

          {PERMISSION_ROWS.map((row) => (
            <View key={row.key} style={styles.permissionRow}>
              <View
                style={styles.permissionLabelCol}
                {...(Platform.OS === 'web'
                  ? { dataSet: { studentNotesSharePermissionLabel: 'true' } }
                  : {})}
              >
                <MaterialIcons name={row.icon} size={22} color={gold} />
                <Text
                  style={styles.permissionLabel}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { studentNotesSharePermissionLabelText: 'true' } }
                    : {})}
                >
                  {row.label}
                </Text>
              </View>
              <PermissionMultiSelect
                students={students}
                selectedIds={draft[row.key]}
                onChange={(ids) => setField(row.key, ids)}
                loading={loadingStudents}
                styles={styles}
                gold={gold}
              />
            </View>
          ))}

          <View
            style={styles.btnRow}
            {...(Platform.OS === 'web'
              ? { dataSet: { studentNotesShareBtnRow: 'true' } }
              : {})}
          >
            <TouchableOpacity
              onPress={onClose}
              disabled={saving}
              activeOpacity={0.85}
              style={styles.btnTouchable}
            >
              <View
                style={[styles.actionBtn, { backgroundColor: BTN_CANCEL_BG }]}
                nativeID="student-notes-share-cancel-btn"
                {...(Platform.OS === 'web'
                  ? { dataSet: { studentNotesShareCancelBtn: 'true' } }
                  : {})}
              >
                <Text style={styles.btnTextDark}>Cancel</Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleSave}
              disabled={saving || !saveEnabled}
              activeOpacity={0.85}
              style={styles.btnTouchable}
            >
              <View
                style={[
                  styles.actionBtn,
                  { backgroundColor: BTN_SAVE_BG },
                  (!saveEnabled || saving) && styles.btnSaveDisabled,
                ]}
                nativeID="student-notes-share-save-btn"
                {...(Platform.OS === 'web'
                  ? { dataSet: { studentNotesShareSaveBtn: 'true' } }
                  : {})}
              >
                {saving ? (
                  <ActivityIndicator color="#000" />
                ) : (
                  <Text style={styles.btnTextDark}>Save</Text>
                )}
              </View>
            </TouchableOpacity>
          </View>
    </Pressable>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View
        style={styles.modalRoot}
        nativeID="student-notes-share-modal-root"
        {...(Platform.OS === 'web' ? { dataSet: { studentNotesShareModalRoot: 'true' } } : {})}
      >
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close share dialog"
          nativeID="student-notes-share-dismiss"
          {...(Platform.OS === 'web' ? { dataSet: { modalBackdrop: 'true' } } : {})}
        />
        <View
          style={styles.backdropCenter}
          pointerEvents="box-none"
          nativeID="student-notes-share-center"
        >
          {sheetContent}
        </View>
      </View>
    </Modal>
  );
}

function getStyles(theme, gold) {
  return StyleSheet.create({
    modalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0, 0, 0, 0.45)',
    },
    backdropCenter: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      backgroundColor: 'transparent',
    },
    sheet: {
      width: '100%',
      maxWidth: 520,
      maxHeight: '90%',
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      borderWidth: 2,
      borderColor: gold,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 8,
          }),
    },
    sheetHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    sheetTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: gold,
      backgroundColor: 'transparent',
    },
    sheetSubtitle: {
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : theme.colors.text,
      marginBottom: 16,
      lineHeight: 20,
    },
    saveFirstHint: {
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : theme.colors.text,
      marginBottom: 16,
      lineHeight: 20,
    },
    permissionRow: {
      marginBottom: 14,
      backgroundColor: 'transparent',
    },
    permissionLabelCol: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    permissionLabel: {
      fontSize: 15,
      fontWeight: '600',
      color: gold,
    },
    pickWrap: {
      position: 'relative',
      zIndex: 1,
    },
    pickTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#555' : '#CCC',
      borderRadius: 8,
      paddingVertical: 10,
      paddingHorizontal: 12,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FAFAFA',
      minHeight: 44,
    },
    pickTriggerText: {
      flex: 1,
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : theme.colors.text,
      marginRight: 8,
      backgroundColor: 'transparent',
    },
    pickList: {
      maxHeight: 160,
      marginTop: 4,
      borderWidth: 1,
      borderColor: gold,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#252525' : '#FFFFFF',
    },
    pickRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#333' : '#EEE',
    },
    pickRowSelected: {
      backgroundColor: theme.isDarkMode ? '#333' : '#FFF8E7',
    },
    pickRowText: {
      flex: 1,
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : theme.colors.text,
      backgroundColor: 'transparent',
    },
    pickEmpty: {
      fontSize: 13,
      color: theme.isDarkMode ? '#CCCCCC' : '#666',
      marginTop: 6,
      paddingHorizontal: 4,
    },
    btnRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 12,
      marginTop: 16,
      backgroundColor: 'transparent',
    },
    btnTouchable: {
      backgroundColor: 'transparent',
    },
    actionBtn: {
      minWidth: 120,
      paddingVertical: 12,
      paddingHorizontal: 22,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 44,
    },
    btnSaveDisabled: {
      opacity: 0.45,
    },
    btnTextDark: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000000',
    },
  });
}
